import { DeserializedServer, Sprint, StrategyOverride } from '@amzn/awscat-portfolio-assessment-types';
import { TableProps } from '@amzn/awsui-components-react';

import { constants } from '../../common/Localization';
import { formatCurrencyNoDecimals, formatNumber } from '../../common/LocalizationHelper';
import { getCell } from '../common/awsui-table-helper';
import { CalculatedStrategy } from './interfaces';

/**
 * Returns the table column definitions for the Migration cost Sprint tabs, formatted with given currency
 * @param currency 
 * @returns ColumnDefinition array of type Sprint
 */
export const getResourceCostColumns = (currency: string): TableProps.ColumnDefinition<Sprint>[] => {
    const columnDefs: TableProps.ColumnDefinition<Sprint>[] = [{
        id: 'strategy',
        header: constants.MIGRATION_PATTERN,
        cell: item => item.strategy,
    }, {
        id: 'sprintsRequired',
        header: constants.TOTAL_SPRINTS_REQUIRED,
        cell: item => getCell(formatNumber(item.sprintsRequired)),
    }, {
        id: 'sprintTeams',
        header: constants.TOTAL_SPRINT_TEAMS,
        cell: item => getCell(formatNumber(item.sprintTeams)),
    }, {
        id: 'totalCustomerResources',
        header: constants.CUSTOMER_RESOURCES_COUNT,
        cell: item => getCell(formatNumber(item.totalCustomerResources)),
    }, {
        id: 'customerProjectManagementCost',
        header: constants.CUSTOMER_PROJECT_MGMT_COST,
        cell: item => getCell(formatCurrencyNoDecimals(item.customerProjectManagementCost, currency)),
    }, {
        id: 'costCustTotal',
        header: constants.CUSTOMER_RESOURCES_COST,
        cell: item => getCell(formatCurrencyNoDecimals(item.costCust + item.costCustSr + item.costCustOffshore + item.costCustSrOffshore, 
            currency)),
    }, {
        id: 'totalPartnerResources',
        header: constants.PARTNER_RESOURCES_COUNT,
        cell: item => getCell(formatNumber(item.totalPartnerResources)),
    }, {
        id: 'partnerProjectManagementCost',
        header: constants.PARTNER_PROJECT_MGMT_COST,
        cell: item => getCell(formatCurrencyNoDecimals(item.partnerProjectManagementCost, currency)),
    }, {
        id: 'costPartnerTotal',
        header: constants.PARTNER_RESOURCES_COST,
        cell: item => getCell(formatCurrencyNoDecimals(item.costPartner + item.costPartnerSr + item.costPartnerOffshore + item.costPartnerSrOffshore,
            currency)),
    }, {
        id: 'totalAwsResources',
        header: constants.AWS_RESOURCES_COUNT,
        cell: item => getCell(formatNumber(item.totalAwsResources)),
    }, {
        id: 'awsProjectManagementCost',
        header: constants.AWS_PROJECT_MGMT_COST,
        cell: item => getCell(formatCurrencyNoDecimals(item.awsProjectManagementCost, currency)),
    }, {
        id: 'costAwsTotal',
        header: constants.AWS_RESOURCES_COST,
        cell: item => getCell(formatCurrencyNoDecimals(item.costAws + item.costAwsSr + item.costAwsOffshore + item.costAwsSrOffshore,
            currency)),
    }, {
        id: 'totalExecutionCost',
        header: constants.TOTAL_RESOURCE_COST,
        cell: item => getCell(formatCurrencyNoDecimals(item.totalExecutionCost, currency), true),
    }];

    return columnDefs;
};

/**
 * Returns true if the given server's migration strategy has been overridden, false if not
 * @param server 
 * @param serverMigrationStrategy 
 * @returns boolean
 */
export const isOverridden = (server: DeserializedServer, serverMigrationStrategy: StrategyOverride | null): boolean => {
    if (serverMigrationStrategy) {
        return serverMigrationStrategy.override ? true : false;
    } else {
        return server.migrationPlanOverride ? true : false;
    }
};

/**
 * Calculate the server strategy
 * @param server 
 * @param serverMigrationStrategy 
 * @returns the calculated strategy based on the server
 */
export const calculateStrategy = (server: DeserializedServer, serverMigrationStrategy: StrategyOverride | null): CalculatedStrategy => {
    if (serverMigrationStrategy) {
        if (serverMigrationStrategy.override) {
            return {
                value: serverMigrationStrategy.override,
                displayTools: false,
            };
        } else if (server.migrationPlan) {
            return {
                value: server.migrationPlan,
                displayTools: false,
            };
        } else {
            return {
                value: serverMigrationStrategy.calculated,
                displayTools: true,
            };
        }
    } else {
        if (server.migrationPlanOverride) {
            return {
                value: server.migrationPlan,
                displayTools: false,
            };
        } else if (server.migrationPlan) {
            return {
                value: server.migrationPlan,
                displayTools: false,
            };
        } else {
            return {
                value: constants.UNKNOWN,
                displayTools: false,
            };
        } 
    }
};
